import { mediaMax } from "components/designV2/breakpoints";
import { Radius, Spacing } from "components/designV2/constants";
import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";
import styled from "styled-components";

export const InvoiceModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.lg}px;
`;

export const InvoiceSection = styled.section`
  background: ${ColorV2.Blue7};
  border-radius: ${Radius.xl}px;
  display: flex;
  flex-direction: column;
  gap: ${Spacing.md}px;
  padding: ${Spacing.lg}px;
`;

export const InvoiceSectionHeader = styled.button<{ $open?: boolean }>`
  align-items: center;
  appearance: none;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  gap: ${Spacing.md}px;
  justify-content: space-between;
  margin: 0;
  padding: 0;
  text-align: left;
  width: 100%;
`;

export const InvoiceSectionTitle = styled.h4`
  ${TypographyV2.h5}
  color: ${ColorV2.Blue1};
  margin: 0;
`;

export const InvoiceSectionSummary = styled.span`
  ${TypographyV2.s1}
  color: ${ColorV2.Blue4};
  margin-left: ${Spacing.xs}px;
`;

export const InvoiceFiltersBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.md}px;
`;

export const InvoiceFieldRow = styled.div`
  display: grid;
  gap: ${Spacing.md}px;
  grid-template-columns: minmax(0, 320px) minmax(0, 1fr);

  ${mediaMax.tablet} {
    grid-template-columns: 1fr;
  }
`;

export const InvoiceField = styled.div`
  min-width: 0;
`;

export const InvoiceCustomerSummary = styled.div`
  display: grid;
  gap: ${Spacing.md}px;
  grid-template-columns: repeat(3, minmax(0, 1fr));

  ${mediaMax.tablet} {
    grid-template-columns: 1fr;
  }
`;

export const ProductSelector = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.sm}px;
  min-width: 0;
`;

export const ProductSelectorLabel = styled.label`
  ${TypographyV2.b2}
  color: ${ColorV2.Blue2};
`;

export const ProductSelectorTrigger = styled.button<{ $isOpen: boolean }>`
  ${TypographyV2.b2}
  align-items: center;
  appearance: none;
  background: ${ColorV2.White};
  border: 1.5px solid
    ${(props) => (props.$isOpen ? ColorV2.Blue4 : ColorV2.Blue5)};
  border-radius: 16px;
  color: ${ColorV2.Blue1};
  cursor: pointer;
  display: flex;
  gap: ${Spacing.sm}px;
  justify-content: space-between;
  min-height: 56px;
  padding: 0 20px;
  text-align: left;
  width: 100%;
`;

export const ProductSelectorValue = styled.span<{ $placeholder?: boolean }>`
  color: ${(props) => (props.$placeholder ? ColorV2.Blue5 : ColorV2.Blue1)};
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ProductSelectorPanel = styled.div`
  background: ${ColorV2.White};
  border: 1.5px solid ${ColorV2.Blue5};
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  gap: ${Spacing.sm}px;
  padding: ${Spacing.md}px;
`;

export const ProductSelectorActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing.sm}px;
`;

export const ProductOptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.sm}px;
  max-height: 260px;
  overflow: auto;
`;

export const ProductOptionRow = styled.div`
  background: ${ColorV2.Blue7};
  border-radius: 12px;
  padding: 12px 14px;

  label {
    width: 100%;
  }
`;

export const InvoiceActions = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export const InvoiceErrorText = styled.div`
  ${TypographyV2.s1}
  color: ${ColorV2.NegativeRed};
`;

export const InvoiceResultsHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xs}px;
`;

export const InvoiceResultsTitle = styled.h4`
  ${TypographyV2.h5}
  color: ${ColorV2.Blue1};
  margin: 0;
`;

export const InvoiceResultsDescription = styled.p`
  ${TypographyV2.s1}
  color: ${ColorV2.Blue4};
  margin: 0;
`;

export const InvoiceTable = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.sm}px;
`;

export const InvoiceTableRow = styled.div`
  background: ${ColorV2.White};
  border: 1px solid ${ColorV2.Blue6};
  border-radius: 16px;
  display: grid;
  gap: ${Spacing.md}px;
  grid-template-columns:
    minmax(160px, 1.2fr) minmax(160px, 1.2fr) minmax(100px, 0.8fr)
    minmax(100px, 0.8fr) minmax(120px, 0.9fr);
  padding: ${Spacing.md}px;

  ${mediaMax.tablet} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  ${mediaMax.mobile} {
    grid-template-columns: 1fr;
  }
`;

export const InvoiceTotalsRow = styled.div`
  background: ${ColorV2.White};
  border: 1px solid ${ColorV2.Blue6};
  border-radius: 16px;
  display: grid;
  gap: ${Spacing.md}px;
  grid-template-columns: minmax(0, 1fr) minmax(100px, 0.8fr) minmax(140px, 1fr);
  padding: ${Spacing.md}px;

  ${mediaMax.tablet} {
    grid-template-columns: 1fr;
  }
`;

export const InvoiceTotalBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const InvoiceTotalLabel = styled.span`
  ${TypographyV2.s1}
  color: ${ColorV2.Blue4};
`;

export const InvoiceTotalValue = styled.span`
  ${TypographyV2.b1}
  color: ${ColorV2.Blue1};
`;
